import useBoxDimensions from '@hooks/use-box-dimensions';
import { Backdrop, Button, Label } from '@hyperflake/react-ui-library';
import { CrossIcon } from '@icon/index';
import { Input } from '@library/forms';
import { CircularProgress } from '@library/loaders/components';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useMemo, useState } from 'react';
import ScrollLock from 'react-scrolllock';

interface ConfimationDialogProps {
    show: boolean;
    onClose: () => void;
    handleDelete: (val: string) => void;
    isLoading: boolean;
    name: string;
}
const modalAnimation = {
    variants: {
        hidden: {
            scale: 0.15,
            opacity: 0,
        },
        visible: {
            scale: 1,
            opacity: 1,
        },
    },
    transition: { duration: 0.35 },
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
};

const ConfirmationDialog = (props: ConfimationDialogProps) => {
    const { show, onClose, handleDelete, isLoading, name } = props;
    const [cameraName, setCameraName] = useState('');

    const { aspectRatio, headerHeight, containerWidth, containerHeight } = useMemo(() => {
        const headerHeight = 60;

        return {
            aspectRatio: 1.49,
            headerHeight: 60,
            containerWidth: window.innerWidth - 96,
            containerHeight: window.innerHeight - 96 - headerHeight,
        };
    }, []);

    const handleClose = () => {
        setCameraName('');
        onClose();
    };

    const { refs, dimensions } = useBoxDimensions({ aspectRatio, containerWidth, containerHeight });
    return (
        <AnimatePresence initial={false} mode="wait">
            {show && (
                <>
                    <Backdrop ref={refs.setReference} show={show} onClick={handleClose} className="z-[30]" />
                    <ScrollLock>
                        <div
                            className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[100] mt-5"
                            style={{ height: dimensions.height + headerHeight, width: dimensions.width }}
                        >
                            <motion.div {...modalAnimation} onClick={(e) => e.stopPropagation()}>
                                <div
                                    className={`flex items-center bg-card-color px-6 rounded-t-2xl`}
                                    style={{ height: headerHeight }}
                                >
                                    <div className="flex items-center w-full gap-6">
                                        <h5 className="font-bold text-grayscale-900">Confirm Delete</h5>

                                        <button
                                            className="ml-auto grid place-items-center w-7 h-7 rounded-full bg-background-color hover-transition hover:bg-primary/10"
                                            onClick={onClose}
                                        >
                                            <CrossIcon className="text-grayscale-500" width="10" height="10" />
                                        </button>
                                    </div>
                                </div>
                                <div className="relative bg-white p-10">
                                    <div className="w-full max-w-md sm:max-w-lg">
                                        <Label className="inline-block text-sm sm:text-base">
                                            To Confirm Delete type:&nbsp;
                                            <span className="font-semibold">DELETE</span>
                                        </Label>

                                        <Input
                                            placeholder="Enter DELETE"
                                            className="mt-2 w-full"
                                            onChange={(e) => setCameraName(e.target.value)}
                                            value={cameraName}
                                        />

                                        <div className="mt-3 grid grid-cols-2 gap-2 sm:mt-4 sm:flex sm:justify-end">
                                            <Button
                                                disabled={isLoading}
                                                onClick={() => onClose()}
                                                variant="default"
                                                color="primary"
                                                className="col-span-2 w-full sm:col-span-1 sm:w-auto"
                                            >
                                                Cancel
                                            </Button>

                                            <Button
                                                disabled={cameraName !== 'DELETE'}
                                                onClick={() => handleDelete(cameraName)}
                                                variant="default"
                                                color="primary"
                                                className="col-span-2 w-full sm:col-span-1 sm:w-auto"
                                            >
                                                {isLoading ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </ScrollLock>
                </>
            )}
        </AnimatePresence>
    );
};

export default ConfirmationDialog;
